// PLTP or  SPLTP - Special/Private Land Timber Permit
//  ISSUANCE OF PRIVATE LAND TIMBER PERMIT (PLTP) FOR NON-PREMIUIM SPECIES,
// OR SPECIAL PLTP (SPLTP) FOR PREMIUM/ NATURALLY-GROWN TREES WITHIN PRIVATE/ TITLED LANDS

import {
   AlertDialog,
   AlertDialogAction,
   AlertDialogCancel,
   AlertDialogContent,
   AlertDialogDescription,
   AlertDialogFooter,
   AlertDialogHeader,
   AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import Modal from '@/components/ui/modal';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import '@/components/ui/styles/customScrollBar.css';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { formatLabel, UploadCard } from '@/pages/user/ApplicationForms/CSAWForm/CSAWFormUtils';
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import { Loader2 } from "lucide-react";
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import FormStepIndicator from '../FormStepIndicator';

const CREATE_PLTP_PERMIT = gql`
   mutation CreatePLTPPermit($input: PLTPPermitInput!) {
      createPLTPPermit(input: $input) {
         id
         applicationNumber
         status
         dateOfSubmission
         requestType
         name
         address
         contactNumber
         treeType
         treeStatus
         landType
         posingDanger
         forPersonalUse
         purpose
         files {
         letterOfIntent { filename contentType }
         lguEndorsement { filename contentType }
         titleCertificate { filename contentType }
         darCertificate { filename contentType }
         specialPowerOfAttorney { filename contentType }
         ptaResolution { filename contentType }
         }
      }
   }
   `;

const SAVE_PLTP_PERMIT_DRAFT = gql`
   mutation SavePLTPPermitDraft($input: PLTPPermitInput!) {
      savePLTPPermitDraft(input: $input) {
         id
         applicationNumber
         status
         files {
         letterOfIntent { filename contentType }
         lguEndorsement { filename contentType }
         titleCertificate { filename contentType }
         darCertificate { filename contentType }
         specialPowerOfAttorney { filename contentType }
         ptaResolution { filename contentType }
         }
      }
   }
   `;

const VERIFY_INVENTORY_REQUEST = gql`
   query VerifyInventoryRequest($applicationNumber: String!, $requestType: String!) {
      verifyInventoryRequest(applicationNumber: $applicationNumber, requestType: $requestType) {
         ... on PLTPPermit {
         id
         applicationNumber
         requestType
         ApprovedInventoryRequest
         name
         address
         contactNumber
         treeType
         treeStatus
         landType
         posingDanger
         forPersonalUse
         purpose
         }
      }
   }
`;

const PLTPForm = () => {
   const navigate = useNavigate();
   const [currentStep, setCurrentStep] = useState(() => {
      const savedStep = localStorage.getItem('pltpFormStep');
      return savedStep ? parseInt(savedStep, 10) : 0;
   });
   const [formData, setFormData] = useState(() => {
      const savedFormData = localStorage.getItem('pltpFormData');
      return savedFormData ? JSON.parse(savedFormData) : {
         applicationType: 'Private Land Timber Permit',
         name: '',
         address: '',
         contactNumber: '',
         requestType: '',
         treeType: [],
         treeStatus: [],
         landType: [],
         posingDanger: false,
         forPersonalUse: false,
         purpose: '',
         isCLOA: false,
         isRepresentative: false,
         isSchoolOrOrganization: false,
         files: {
            letterOfIntent: [],
            applicationLetter: [],
            lguEndorsement: [],
            titleCertificate: [],
            darCertificate: [],
            specialPowerOfAttorney: [],
            ptaResolution: []
         },
         dateOfSubmission: '',
         status: '',
      };
   });
   const [modalOpen, setModalOpen] = useState(false);
   const [modalContent, setModalContent] = useState({ title: '', message: '' });
   const [createPLTPPermit] = useMutation(CREATE_PLTP_PERMIT);
   const [savePLTPPermitDraft] = useMutation(SAVE_PLTP_PERMIT_DRAFT);
   const [isSubmitting, setIsSubmitting] = useState(false);
   const [isSavingDraft, setIsSavingDraft] = useState(false);
   const [showSubmitDialog, setShowSubmitDialog] = useState(false);
   const [showDraftDialog, setShowDraftDialog] = useState(false);
   const [previousApplicationNumber, setPreviousApplicationNumber] = useState('');
   const [verifiedInventoryData, setVerifiedInventoryData] = useState(null);
   const [isVerifyingRequest, setIsVerifyingRequest] = useState(false);
   const [verificationEnabled, setVerificationEnabled] = useState(() => {
      const enabled = localStorage.getItem('verificationEnabled');
      return enabled === null ? true : enabled === 'true';
   });

   const [verifyInventoryRequest] = useLazyQuery(VERIFY_INVENTORY_REQUEST, {
      onCompleted: (data) => {
         if (data?.verifyInventoryRequest) {
            setVerifiedInventoryData(data.verifyInventoryRequest);
            toast.success('Inventory request verified successfully', {
               description: 'You can now proceed with your cutting permit application'
            });
            // Pre-fill form data from inventory request
            setFormData(prev => ({
               ...prev,
               name: data.verifyInventoryRequest.name,
               address: data.verifyInventoryRequest.address,
               contactNumber: data.verifyInventoryRequest.contactNumber,
               treeType: data.verifyInventoryRequest.treeType,
               treeStatus: data.verifyInventoryRequest.treeStatus,
               landType: data.verifyInventoryRequest.landType,
               posingDanger: data.verifyInventoryRequest.posingDanger,
               forPersonalUse: data.verifyInventoryRequest.forPersonalUse,
               purpose: data.verifyInventoryRequest.purpose
            }));
         }
         setIsVerifyingRequest(false);
      },
      onError: (error) => {
         toast.error(error.message || 'Failed to verify inventory request');
         setIsVerifyingRequest(false);
         setPreviousApplicationNumber('');
      }
   });

   const handleInputChange = (e) => {
      const { name, value } = e.target;
      setFormData(prev => ({ ...prev, [name]: value }));
   };

   const handleCheckboxChange = (e) => {
      const { name, checked } = e.target;

      // Map of checkbox names to their corresponding file types
      const fileTypeMap = {
         isCLOA: 'darCertificate',
         isRepresentative: 'specialPowerOfAttorney',
         isSchoolOrOrganization: 'ptaResolution'
      };

      // Clear associated files if requirement is unchecked
      if (!checked && fileTypeMap[name]) {
         setFormData(prev => ({
            ...prev,
            [name]: checked,
            files: {
               ...prev.files,
               [fileTypeMap[name]]: [] // Clear the files for this requirement
            }
         }));
      } else {
         setFormData(prev => ({ ...prev, [name]: checked }));
      }
   };

   const handleFileChange = (e, documentType) => {
      if (e.target.files) {
         const newFiles = Array.from(e.target.files);
         setFormData(prev => ({
            ...prev,
            files: {
               ...prev.files,
               [documentType]: [...(prev.files[documentType] || []), ...newFiles]
            }
         }));
      }
   };

   const removeFile = (documentType, fileToRemove) => {
      setFormData(prev => ({
         ...prev,
         files: {
            ...prev.files,
            [documentType]: prev.files[documentType].filter(file => file !== fileToRemove)
         }
      }));
   };

   const handleVerifyInventoryRequest = async () => {
      if (!previousApplicationNumber) {
         toast.error("Please enter an application number");
         return;
      }

      // Proper regex pattern for PLTP application numbers
      const permitTypeMatch = previousApplicationNumber.match(/^PMDQ-PLTP-\d{4}-\d{4}-\d{6}$/);
      if (!permitTypeMatch) {
         toast.error("Invalid PLTP application number format");
         return;
      }

      setIsVerifyingRequest(true);
      try {
         await verifyInventoryRequest({
            variables: {
               applicationNumber: previousApplicationNumber,
               requestType: formData.requestType
            }
         });
      } catch (error) {
         console.error('Error verifying inventory request:', error);
         toast.error(error.message || 'Failed to verify inventory request');
         setPreviousApplicationNumber('');
      }
   };

   const handleNextStep = () => {
      if (currentStep === 0) {
         if (!formData.requestType) {
            toast.error("Please select a request type");
            return;
         }
         const verificationEnabled = localStorage.getItem('verificationEnabled') !== 'false';
         if (verificationEnabled && formData.requestType === 'Cutting' && !verifiedInventoryData) {
            toast.error("Please verify your previous inventory request first");
            return;
         }
      }

      // Skip Tree and Land Details step for Cutting requests
      if (currentStep === 1 && formData.requestType === 'Cutting') {
         setCurrentStep(3); // Skip to Document Requirements
      } else if (currentStep === 2 && formData.requestType === 'Inventory') {
         setCurrentStep(4); // Skip to Upload Documents for Inventory
      } else {
         setCurrentStep(prev => prev + 1);
      }
   };

   const handlePrevStep = () => {
      // Handle going back from Document Requirements to Applicant Details for Cutting requests
      if (currentStep === 3 && formData.requestType === 'Cutting') {
         setCurrentStep(1);
      } else if (currentStep === 4 && formData.requestType === 'Inventory') {
         setCurrentStep(2);
      } else {
         setCurrentStep(prev => prev - 1);
      }
   };

   const handleSaveAsDraft = () => {
      setShowDraftDialog(true);
   };

   const handleConfirmSaveAsDraft = async () => {
      try {
         setIsSavingDraft(true);
         const input = {
            name: formData.name,
            address: formData.address,
            contactNumber: formData.contactNumber,
            requestType: formData.requestType,
            treeType: formData.treeType,
            treeStatus: formData.treeStatus,
            landType: formData.landType,
            posingDanger: formData.posingDanger,
            forPersonalUse: formData.forPersonalUse,
            purpose: formData.purpose,
            files: {}
         };

         // Process files
         for (const [key, files] of Object.entries(formData.files)) {
            if (files.length > 0) {
               input.files[key] = await Promise.all(files.map(async (file) => {
                  try {
                     // Skip processing if the file is already in the correct format (metadata object)
                     if (file.isMetadataOnly || !(file instanceof File)) {
                        return {
                           filename: file.filename || file.name,
                           contentType: file.type,
                           data: file.data || ''
                        };
                     }
                     // Process actual File objects
                     const content = await readFileAsBase64(file);
                     return {
                        filename: file.name,
                        contentType: file.type,
                        data: content
                     };
                  } catch (error) {
                     console.error('Error processing file:', error);
                     toast.error(`Error processing file ${file.name || file.filename}`);
                     throw error;
                  }
               }));
            }
         }

         const token = localStorage.getItem('token');
         const { data } = await savePLTPPermitDraft({
            variables: { input },
            context: {
               headers: {
                  'Apollo-Require-Preflight': 'true',
                  'Authorization': `Bearer ${token}`,
               },
            },
         });

         if (data.savePLTPPermitDraft) {
            setModalContent({
               title: 'Draft saved successfully!',
               message: 'Do you want to view your applications?'
            });
            setModalOpen(true);

            localStorage.removeItem('pltpFormStep');
            localStorage.removeItem('pltpFormData');
         }
      } catch (error) {
         console.error('Error saving draft:', error);
         toast.error("Error saving draft: " + error.message);
      } finally {
         setIsSavingDraft(false);
         setShowDraftDialog(false);
      }
   };

   const handleSubmit = async (e) => {
      if (e) e.preventDefault();
      setShowSubmitDialog(true);
   };

   const handleConfirmSubmit = async () => {
      try {
         setIsSubmitting(true);
         const input = {
            name: formData.name,
            address: formData.address,
            contactNumber: formData.contactNumber,
            requestType: formData.requestType,
            treeType: formData.treeType,
            treeStatus: formData.treeStatus,
            landType: formData.landType,
            posingDanger: formData.posingDanger,
            forPersonalUse: formData.forPersonalUse,
            purpose: formData.purpose,
            verifiedInventoryId: formData.requestType === 'Cutting' && verifiedInventoryData ?
               verifiedInventoryData.id : undefined,
            files: {}
         };

         // Process files
         for (const [key, files] of Object.entries(formData.files)) {
            if (files.length > 0) {
               input.files[key] = await Promise.all(files.map(async (file) => {
                  try {
                     // Skip processing if the file is already in the correct format (metadata object)
                     if (file.isMetadataOnly || !(file instanceof File)) {
                        return {
                           filename: file.filename || file.name,
                           contentType: file.type,
                           data: file.data || ''
                        };
                     }
                     // Process actual File objects
                     const content = await readFileAsBase64(file);
                     return {
                        filename: file.name,
                        contentType: file.type,
                        data: content
                     };
                  } catch (error) {
                     console.error('Error processing file:', error);
                     toast.error(`Error processing file ${file.name || file.filename}`);
                     throw error;
                  }
               }));
            }
         }

         console.log('Submitting PLTP application with input:', input);

         const token = localStorage.getItem('token');
         const { data } = await createPLTPPermit({
            variables: { input },
            context: {
               headers: {
                  'Apollo-Require-Preflight': 'true',
                  'Authorization': `Bearer ${token}`,
               },
            },
         });

         console.log('Created PLTP permit:', data);

         if (data.createPLTPPermit) {
            setModalContent({
               title: 'Application submitted successfully!',
               message: 'Do you want to view your application?'
            });
            setModalOpen(true);

            localStorage.removeItem('pltpFormStep');
            localStorage.removeItem('pltpFormData');
         }
      } catch (error) {
         console.error('Error submitting application:', error);
         toast.error("Error submitting application: " + error.message);
      } finally {
         setIsSubmitting(false);
         setShowSubmitDialog(false);
      }
   };

   // Helper function to read file as base64
   const readFileAsBase64 = (file) => {
      return new Promise((resolve, reject) => {
         try {
            if (!(file instanceof File)) {
               reject(new Error('Invalid file object'));
               return;
            }
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result.split(',')[1]);
            reader.onerror = error => reject(error);
            reader.readAsDataURL(file);
         } catch (error) {
            reject(error);
         }
      });
   };

   const steps = [
      { title: "Select Request Type", description: "Choose the type of request" },
      { title: "Applicant Details", description: "Fill in your personal information" },
      {
         title: formData.requestType === 'Cutting' ? "Purpose" : "Tree and Land Details",
         description: formData.requestType === 'Cutting' ? "Specify the purpose for cutting" : "Provide information about the trees and land"
      },
      { title: "Document Requirements", description: "Specify document requirements" },
      { title: "Upload Documents", description: "Upload necessary documents" },
      { title: "Review Your Application", description: "Review and submit your application" },
   ];

   const CheckboxItem = ({ id, label, checked, onChange }) => (
      <div className="flex items-center space-x-2">
         <Checkbox
            id={id}
            name={id}
            checked={checked}
            onCheckedChange={(checked) => onChange({ target: { name: id, checked } })}
         />
         <Label htmlFor={id} className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
            {label}
         </Label>
      </div>
   );

   const renderInventoryVerificationSection = () => (
      <div className="space-y-4">
         <div className="bg-white dark:bg-gray-800 rounded-lg border p-6">
            <div className="space-y-4">
               {/* Info Section */}
               <div className="bg-blue-50 dark:bg-blue-900/20 rounded-md p-4 mb-6">
                  <div className="flex items-start">
                     <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                           <circle cx="12" cy="12" r="10" />
                           <line x1="12" y1="16" x2="12" y2="12" />
                           <line x1="12" y1="8" x2="12.01" y2="8" />
                        </svg>
                     </div>
                     <div className="ml-3">
                        <h3 className="text-sm font-medium text-blue-800 dark:text-blue-300">
                           Before you proceed with Cutting request
                        </h3>
                        <div className="mt-2 text-sm text-blue-700 dark:text-blue-400">
                           <ul className="list-disc pl-5 space-y-1">
                              <li>You must have a completed inventory request</li>
                              <li>The inventory request must be under your name</li>
                              <li>The application number format should be: PMDQ-PLTP-XXXX</li>
                              <li>
                                 You can check your completed inventory requests{' '}
                                 <Link
                                    to="/applicationsStatus?tab=Applications&subtab=Completed"
                                    className="text-blue-600 dark:text-blue-300 hover:underline font-medium inline-flex items-center gap-1"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                 >
                                    <span>click here to view</span>
                                    <svg
                                       className="h-4 w-4"
                                       xmlns="http://www.w3.org/2000/svg"
                                       viewBox="0 0 24 24"
                                       fill="none"
                                       stroke="currentColor"
                                       strokeWidth="2"
                                       strokeLinecap="round"
                                       strokeLinejoin="round"
                                    >
                                       <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                                       <polyline points="15 3 21 3 21 9" />
                                       <line x1="10" y1="14" x2="21" y2="3" />
                                    </svg>
                                 </Link>
                              </li>
                           </ul>
                        </div>
                     </div>
                  </div>
               </div>

               {/* Input Section */}
               <div className="space-y-3">
                  <Label className="text-base font-semibold">
                     Previous Inventory Application Number
                  </Label>
                  <div className="flex gap-3">
                     <div className="flex-1">
                        <Input
                           type="text"
                           value={previousApplicationNumber}
                           onChange={(e) => setPreviousApplicationNumber(e.target.value)}
                           placeholder="e.g., PMDQ-PLTP-2024-0001"
                           disabled={isVerifyingRequest || verifiedInventoryData}
                           required
                           className="h-11 text-base"
                        />
                     </div>
                     <Button
                        type="button"
                        variant={verifiedInventoryData ? "outline" : "default"}
                        size="lg"
                        onClick={handleVerifyInventoryRequest}
                        disabled={isVerifyingRequest || verifiedInventoryData || !previousApplicationNumber.trim()}
                        className={`min-w-[120px] ${verifiedInventoryData
                           ? "border-green-500 text-green-600"
                           : "bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white"
                           }`}
                     >
                        {isVerifyingRequest ? (
                           <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Verifying
                           </>
                        ) : verifiedInventoryData ? (
                           <>
                              <svg className="mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                 <path d="M20 6L9 17l-5-5" />
                              </svg>
                              Verified
                           </>
                        ) : (
                           'Verify Request'
                        )}
                     </Button>
                  </div>
               </div>

               {/* Success Message */}
               {verifiedInventoryData && (
                  <div className="mt-4 bg-green-50 dark:bg-green-900/20 rounded-lg p-4 border border-green-200 dark:border-green-800">
                     <div className="flex">
                        <div className="flex-shrink-0">
                           <svg className="h-5 w-5 text-green-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                              <polyline points="22 4 12 14.01 9 11.01" />
                           </svg>
                        </div>
                        <div className="ml-3">
                           <h3 className="text-sm font-medium text-green-800 dark:text-green-400">
                              Inventory Request Verified Successfully
                           </h3>
                           <div className="mt-2 text-sm text-green-700 dark:text-green-500">
                              <p>You can now proceed with your cutting request. Some details will be pre-filled based on your inventory request.</p>
                           </div>
                        </div>
                     </div>
                  </div>
               )}

               {!verificationEnabled && (
                  <div className="mt-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4 border border-yellow-200 dark:border-yellow-800">
                     <div className="flex">
                        <div className="flex-shrink-0">
                           <svg className="h-5 w-5 text-yellow-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                              <line x1="12" y1="9" x2="12" y2="13" />
                              <line x1="12" y1="17" x2="12.01" y2="17" />
                           </svg>
                        </div>
                        <div className="ml-3">
                           <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-400">
                              Development Mode
                           </h3>
                           <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-500">
                              <p>Verification check is currently disabled in development settings.</p>
                           </div>
                        </div>
                     </div>
                  </div>
               )}
            </div>
         </div>
      </div>
   );

   useEffect(() => {
      localStorage.setItem('pltpFormStep', currentStep.toString());
   }, [currentStep]);

   useEffect(() => {
      localStorage.setItem('pltpFormData', JSON.stringify(formData));
   }, [formData]);

   useEffect(() => {
      const handleBeforeUnload = (e) => {
         // If it's a refresh, persist the data
         if (e.persisted) {
            return;
         }
      };

      window.addEventListener('beforeunload', handleBeforeUnload);

      return () => {
         window.removeEventListener('beforeunload', handleBeforeUnload);

         // Get the navigation type
         const perfEntries = performance.getEntriesByType('navigation');
         const navType = perfEntries.length > 0 ? perfEntries[0].type : '';

         // Only clear if it's not a refresh/reload
         if (navType !== 'reload') {
            // Clear form data from localStorage when component unmounts (not on refresh)
            localStorage.removeItem('pltpFormStep');
            localStorage.removeItem('pltpFormData');
            // Reset form data to initial state
            setFormData({
               applicationType: 'Private Land Tree Plantation',
               name: '',
               address: '',
               cellphone: '',
               purpose: '',
               isWithinTimberland: false,
               isTitledProperty: false,
               isWithinProtectedArea: false,
               documentRequirements: {
                  isWithinTimberland: false,
                  isTitledProperty: false,
                  isWithinProtectedArea: false
               },
               files: {
                  letterOfIntent: [],
                  validId: [],
                  landTenurial: [],
                  ownerConsent: [],
                  pambClearance: [],
                  proofOfOwnership: [],
                  sketchPlan: [],
                  plantationLocation: [],
                  inventoryReport: []
               },
               dateOfSubmission: '',
               status: ''
            });
            // Reset current step
            setCurrentStep(0);
         }
      };
   }, []);

   const formatReviewValue = (key, value) => {
      if (typeof value === 'boolean') {
         return value ? 'Yes' : 'No';
      }
      if (Array.isArray(value)) {
         return value.length > 0 ? value.join(', ') : 'None';
      }
      if (typeof value === 'object' && value !== null) {
         return JSON.stringify(value);
      }
      return value || 'Not specified';
   };

   return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white dark:from-gray-900 dark:to-gray-800 transition-colors duration-300 flex flex-col justify-between pt-[83px]">
         <div className="container mx-auto px-2 sm:px-4 pb-8">
            <div className="max-w-4xl mx-auto">
               {/* Header Section */}
               <div className="text-center mb-4 sm:mb-8">
                  <h1 className="text-2xl sm:text-3xl md:text-4xl font-[700] bg-gradient-to-r from-green-700 to-green-500 dark:from-green-500 dark:to-green-300 bg-clip-text text-transparent leading-tight">
                     Private Land Timber Permit
                  </h1>
                  <h2 className="text-xl sm:text-2xl md:text-3xl font-[700] bg-gradient-to-r from-green-700 to-green-500 dark:from-green-500 dark:to-green-300 bg-clip-text text-transparent mb-2">
                     Application
                  </h2>
                  <p className="mt-2 text-sm sm:text-base text-gray-600 dark:text-gray-400">Complete all steps to submit your application</p>
               </div>

               {/* Step Indicator - Hidden on Mobile */}
               <div className="hidden sm:block mb-8 bg-white dark:bg-gray-800 rounded-xl shadow-md p-2">
                  <FormStepIndicator currentStep={currentStep + 1} formType="PLTP" />
               </div>

               {/* Mobile Step Progress */}
               <div className="sm:hidden mb-4">
                  <div className="flex items-center justify-between px-2">
                     <div className="text-sm text-gray-600 dark:text-gray-400">
                        Step {currentStep + 1} of {steps.length}
                     </div>
                     <div className="text-sm font-medium text-green-600 dark:text-green-400">
                        {steps[currentStep]?.title}
                     </div>
                  </div>
                  <div className="mt-2 h-1 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                     <div
                        className="h-full bg-gradient-to-r from-green-600 to-green-500 transition-all duration-300"
                        style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
                     />
                  </div>
               </div>

               {/* Main Form Card */}
               <Card className="shadow-xl bg-white/80 dark:bg-gray-800/50 backdrop-blur-sm border-0 rounded-xl overflow-hidden">
                  <CardHeader className="border-b border-gray-100 dark:border-gray-700 p-4 sm:p-6">
                     <CardTitle className="flex items-center text-lg sm:text-xl text-green-700 dark:text-green-500">
                        {steps[currentStep]?.title}
                     </CardTitle>
                     <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {steps[currentStep]?.description}
                     </p>
                  </CardHeader>
                  <CardContent className="p-4 sm:p-6">
                     <form onSubmit={handleSubmit} className="space-y-6">
                        {currentStep === 0 && (
                           <div className="space-y-4">
                              <RadioGroup
                                 name="requestType"
                                 value={formData.requestType}
                                 onValueChange={(value) => handleInputChange({ target: { name: 'requestType', value } })}
                                 className="space-y-3"
                              >
                                 <div
                                    className={cn(
                                       `w-full p-4 rounded-md transition-colors cursor-pointer`,
                                       formData.requestType === 'Cutting'
                                          ? 'bg-green-100 hover:bg-green-50 dark:bg-green-900 dark:hover:bg-green-800'
                                          : 'bg-gray-100 hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-600'
                                    )}
                                 >
                                    <div className="flex items-center space-x-3">
                                       <RadioGroupItem value="Cutting" id="Cutting" className="text-green-600 dark:text-green-400" />
                                       <Label
                                          htmlFor="Cutting"
                                          className={`text-base font-medium cursor-pointer
                                             ${formData.requestType === 'Cutting'
                                                ? 'text-green-700 dark:text-green-300'
                                                : 'text-gray-700 dark:text-gray-300'}`}
                                       >
                                          Request for Cutting
                                       </Label>
                                    </div>
                                 </div>

                                 <div
                                    className={cn(
                                       `w-full p-4 rounded-md transition-colors cursor-pointer`,
                                       formData.requestType === 'Inventory'
                                          ? 'bg-green-100 hover:bg-green-50 dark:bg-green-900 dark:hover:bg-green-800'
                                          : 'bg-gray-100 hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-600'
                                    )}
                                 >
                                    <div className="flex items-center space-x-3">
                                       <RadioGroupItem value="Inventory" id="Inventory" className="text-green-600 dark:text-green-400" />
                                       <Label
                                          htmlFor="Inventory"
                                          className={`text-base font-medium cursor-pointer
                                             ${formData.requestType === 'Inventory'
                                                ? 'text-green-700 dark:text-green-300'
                                                : 'text-gray-700 dark:text-gray-300'}`}
                                       >
                                          Request for Inventory of Trees
                                       </Label>
                                    </div>
                                 </div>
                              </RadioGroup>

                              {formData.requestType === 'Cutting' && renderInventoryVerificationSection()}
                           </div>
                        )}

                        {currentStep === 1 && (
                           <div className="space-y-4">
                              <div className="bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-gray-100 dark:border-gray-700">
                                 <h3 className="text-base sm:text-lg font-semibold mb-4 bg-gradient-to-r from-green-700 to-green-500 dark:from-green-500 dark:to-green-300 bg-clip-text text-transparent">
                                    Personal Information
                                 </h3>
                                 <div className="space-y-4">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                       <div className="space-y-2">
                                          <Label htmlFor="name" className="text-sm font-medium text-gray-700 dark:text-gray-300">Name</Label>
                                          <Input
                                             id="name"
                                             name="name"
                                             value={formData.name}
                                             onChange={handleInputChange}
                                             required
                                             className="border-2 rounded-md bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm focus:ring-0 focus:border-green-500 transition-colors duration-200"
                                          />
                                       </div>
                                       <div className="space-y-2">
                                          <Label htmlFor="contactNumber" className="text-sm font-medium text-gray-700 dark:text-gray-300">Contact Number</Label>
                                          <Input
                                             id="contactNumber"
                                             name="contactNumber"
                                             value={formData.contactNumber}
                                             onChange={handleInputChange}
                                             required
                                             className="border-2 rounded-md bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm focus:ring-0 focus:border-green-500 transition-colors duration-200"
                                          />
                                       </div>
                                    </div>
                                    <div className="space-y-2">
                                       <Label htmlFor="address" className="text-sm font-medium text-gray-700 dark:text-gray-300">Address</Label>
                                       <Input
                                          id="address"
                                          name="address"
                                          value={formData.address}
                                          onChange={handleInputChange}
                                          required
                                          className="border-2 rounded-md bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm focus:ring-0 focus:border-green-500 transition-colors duration-200"
                                       />
                                    </div>
                                    {formData.requestType === 'Cutting' && (
                                       <div className="space-y-2">
                                          <Label htmlFor="purpose" className="text-sm font-medium text-gray-700 dark:text-gray-300">Purpose</Label>
                                          <Textarea
                                             id="purpose"
                                             name="purpose"
                                             value={formData.purpose}
                                             onChange={handleInputChange}
                                             required
                                             className="min-h-[80px] sm:min-h-[100px] border-2 rounded-md bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm focus:ring-0 focus:border-green-500 transition-colors duration-200"
                                             placeholder="Please state the purpose for your cutting request..."
                                          />
                                       </div>
                                    )}
                                 </div>
                              </div>
                           </div>
                        )}

                        {currentStep === 2 && (
                           <div className="space-y-6">
                              {formData.requestType === 'Inventory' && (
                                 <div className="h-[calc(100vh-24rem)] sm:h-[500px] overflow-y-auto pr-2 sm:pr-3 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:bg-gray-200 dark:[&::-webkit-scrollbar-thumb]:bg-gray-700 [&::-webkit-scrollbar-thumb]:rounded-full">
                                    <div className="bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-gray-100 dark:border-gray-700">
                                       <h3 className="text-base sm:text-lg font-semibold mb-4 bg-gradient-to-r from-green-700 to-green-500 dark:from-green-500 dark:to-green-300 bg-clip-text text-transparent">
                                          Tree Type
                                       </h3>
                                       <div className="space-y-3">
                                          <div className="flex items-center space-x-2">
                                             <Checkbox
                                                id="plantedTrees"
                                                checked={formData.treeType.includes('Planted Trees')}
                                                onCheckedChange={(checked) => {
                                                   const newTreeType = checked
                                                      ? [...formData.treeType, 'Planted Trees']
                                                      : formData.treeType.filter(type => type !== 'Planted Trees');
                                                   setFormData(prev => ({ ...prev, treeType: newTreeType }));
                                                }}
                                             />
                                             <Label htmlFor="plantedTrees" className="text-sm font-medium text-gray-700 dark:text-gray-300">Planted Trees</Label>
                                          </div>
                                          <div className="flex items-center space-x-2">
                                             <Checkbox
                                                id="naturallyGrown"
                                                checked={formData.treeType.includes('Naturally Grown')}
                                                onCheckedChange={(checked) => {
                                                   const newTreeType = checked
                                                      ? [...formData.treeType, 'Naturally Grown']
                                                      : formData.treeType.filter(type => type !== 'Naturally Grown');
                                                   setFormData(prev => ({ ...prev, treeType: newTreeType }));
                                                }}
                                             />
                                             <Label htmlFor="naturallyGrown" className="text-sm font-medium text-gray-700 dark:text-gray-300">Naturally Grown</Label>
                                          </div>
                                       </div>
                                    </div>

                                    <div className="bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-gray-100 dark:border-gray-700 mt-4">
                                       <h3 className="text-base sm:text-lg font-semibold mb-4 bg-gradient-to-r from-green-700 to-green-500 dark:from-green-500 dark:to-green-300 bg-clip-text text-transparent">
                                          Tree Status
                                       </h3>
                                       <div className="space-y-3">
                                          <div className="flex items-center space-x-2">
                                             <Checkbox
                                                id="standing"
                                                checked={formData.treeStatus.includes('Standing')}
                                                onCheckedChange={(checked) => {
                                                   const newTreeStatus = checked
                                                      ? [...formData.treeStatus, 'Standing']
                                                      : formData.treeStatus.filter(status => status !== 'Standing');
                                                   setFormData(prev => ({ ...prev, treeStatus: newTreeStatus }));
                                                }}
                                             />
                                             <Label htmlFor="standing" className="text-sm font-medium text-gray-700 dark:text-gray-300">Standing</Label>
                                          </div>
                                          <div className="flex items-center space-x-2">
                                             <Checkbox
                                                id="blownDown"
                                                checked={formData.treeStatus.includes('Blown Down')}
                                                onCheckedChange={(checked) => {
                                                   const newTreeStatus = checked
                                                      ? [...formData.treeStatus, 'Blown Down']
                                                      : formData.treeStatus.filter(status => status !== 'Blown Down');
                                                   setFormData(prev => ({ ...prev, treeStatus: newTreeStatus }));
                                                }}
                                             />
                                             <Label htmlFor="blownDown" className="text-sm font-medium text-gray-700 dark:text-gray-300">Blown Down</Label>
                                          </div>
                                       </div>
                                    </div>

                                    <div className="bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-gray-100 dark:border-gray-700 mt-4">
                                       <h3 className="text-base sm:text-lg font-semibold mb-4 bg-gradient-to-r from-green-700 to-green-500 dark:from-green-500 dark:to-green-300 bg-clip-text text-transparent">
                                          Land Type
                                       </h3>
                                       <div className="space-y-3">
                                          <div className="flex items-center space-x-2">
                                             <Checkbox
                                                id="privateLand"
                                                checked={formData.landType.includes('Within Private Land')}
                                                onCheckedChange={(checked) => {
                                                   const newLandType = checked
                                                      ? [...formData.landType, 'Within Private Land']
                                                      : formData.landType.filter(type => type !== 'Within Private Land');
                                                   setFormData(prev => ({ ...prev, landType: newLandType }));
                                                }}
                                             />
                                             <Label htmlFor="privateLand" className="text-sm font-medium text-gray-700 dark:text-gray-300">Within Private Land</Label>
                                          </div>
                                          <div className="flex items-center space-x-2">
                                             <Checkbox
                                                id="tenuredForestLand"
                                                checked={formData.landType.includes('Within Tenured Forest Land')}
                                                onCheckedChange={(checked) => {
                                                   const newLandType = checked
                                                      ? [...formData.landType, 'Within Tenured Forest Land']
                                                      : formData.landType.filter(type => type !== 'Within Tenured Forest Land');
                                                   setFormData(prev => ({ ...prev, landType: newLandType }));
                                                }}
                                             />
                                             <Label htmlFor="tenuredForestLand" className="text-sm font-medium text-gray-700 dark:text-gray-300">Within Tenured Forest Land</Label>
                                          </div>
                                       </div>
                                    </div>

                                    <div className="bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-gray-100 dark:border-gray-700 mt-4">
                                       <h3 className="text-base sm:text-lg font-semibold mb-4 bg-gradient-to-r from-green-700 to-green-500 dark:from-green-500 dark:to-green-300 bg-clip-text text-transparent">
                                          Additional Characteristics
                                       </h3>
                                       <div className="space-y-3">
                                          <div className="flex items-center space-x-2">
                                             <Checkbox
                                                id="posingDanger"
                                                checked={formData.posingDanger}
                                                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, posingDanger: checked }))}
                                             />
                                             <Label htmlFor="posingDanger" className="text-sm font-medium text-gray-700 dark:text-gray-300">Posing Danger</Label>
                                          </div>
                                          <div className="flex items-center space-x-2">
                                             <Checkbox
                                                id="forPersonalUse"
                                                checked={formData.forPersonalUse}
                                                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, forPersonalUse: checked }))}
                                             />
                                             <Label htmlFor="forPersonalUse" className="text-sm font-medium text-gray-700 dark:text-gray-300">For Personal Use</Label>
                                          </div>
                                       </div>
                                    </div>

                                    <div className="bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-gray-100 dark:border-gray-700 mt-4">
                                       <h3 className="text-base sm:text-lg font-semibold mb-4 bg-gradient-to-r from-green-700 to-green-500 dark:from-green-500 dark:to-green-300 bg-clip-text text-transparent">
                                          Purpose
                                       </h3>
                                       <div className="space-y-2">
                                          <Textarea
                                             id="purpose"
                                             name="purpose"
                                             value={formData.purpose}
                                             onChange={handleInputChange}
                                             required
                                             className="min-h-[80px] sm:min-h-[100px] border-2 rounded-md bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm focus:ring-0 focus:border-green-500 transition-colors duration-200"
                                             placeholder="Please state your purpose..."
                                          />
                                       </div>
                                    </div>
                                 </div>
                              )}
                           </div>
                        )}

                        {currentStep === 3 && formData.requestType !== 'Inventory' && (
                           <div className="space-y-4">
                              <div className="bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-gray-100 dark:border-gray-700">
                                 <h3 className="text-base sm:text-lg font-semibold mb-4 bg-gradient-to-r from-green-700 to-green-500 dark:from-green-500 dark:to-green-300 bg-clip-text text-transparent">
                                    Document Requirements
                                 </h3>
                                 <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                                    Please check the boxes that apply to you. In the next step, you will be required to upload the corresponding documents.
                                 </p>
                                 <div className="space-y-3">
                                    <CheckboxItem
                                       id="isCLOA"
                                       label="Is the Title Certificate of Land Ownership Award (CLOA)?"
                                       checked={formData.isCLOA}
                                       onChange={handleCheckboxChange}
                                    />
                                    <CheckboxItem
                                       id="isRepresentative"
                                       label="Are you a representative?"
                                       checked={formData.isRepresentative}
                                       onChange={handleCheckboxChange}
                                    />
                                    <CheckboxItem
                                       id="isSchoolOrOrganization"
                                       label="Is this for a school or organization?"
                                       checked={formData.isSchoolOrOrganization}
                                       onChange={handleCheckboxChange}
                                    />
                                 </div>
                              </div>
                           </div>
                        )}

                        {currentStep === 4 && (
                           <div className="space-y-4">
                              <div className="h-[calc(100vh-24rem)] sm:h-[500px] overflow-y-auto pr-2 sm:pr-3 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:bg-gray-200 dark:[&::-webkit-scrollbar-thumb]:bg-gray-700 [&::-webkit-scrollbar-thumb]:rounded-full">
                                 <div className="space-y-4">
                                    {formData.requestType === 'Inventory' ? (
                                       <UploadCard
                                          label="Letter of Intent"
                                          documentLabel="Upload Letter of Intent (1 original)"
                                          files={formData.files.letterOfIntent}
                                          onFileChange={(e) => handleFileChange(e, 'letterOfIntent')}
                                          onRemoveFile={(file) => removeFile('letterOfIntent', file)}
                                       />
                                    ) : (
                                       <>
                                          <UploadCard
                                             label="Application Letter"
                                             documentLabel="Upload Application Letter (1 original)"
                                             files={formData.files.applicationLetter}
                                             onFileChange={(e) => handleFileChange(e, 'applicationLetter')}
                                             onRemoveFile={(file) => removeFile('applicationLetter', file)}
                                          />
                                          <UploadCard
                                             label="LGU Endorsement"
                                             documentLabel="Upload LGU Endorsement (1 original)"
                                             files={formData.files.lguEndorsement}
                                             onFileChange={(e) => handleFileChange(e, 'lguEndorsement')}
                                             onRemoveFile={(file) => removeFile('lguEndorsement', file)}
                                          />
                                          <UploadCard
                                             label="Title Certificate"
                                             documentLabel="Upload Title Certificate (1 original)"
                                             files={formData.files.titleCertificate}
                                             onFileChange={(e) => handleFileChange(e, 'titleCertificate')}
                                             onRemoveFile={(file) => removeFile('titleCertificate', file)}
                                          />
                                          {formData.isCLOA && (
                                             <UploadCard
                                                label="DAR Certificate"
                                                documentLabel="Upload DAR Certificate (1 original)"
                                                files={formData.files.darCertificate}
                                                onFileChange={(e) => handleFileChange(e, 'darCertificate')}
                                                onRemoveFile={(file) => removeFile('darCertificate', file)}
                                             />
                                          )}
                                          {formData.isRepresentative && (
                                             <UploadCard
                                                label="Special Power of Attorney"
                                                documentLabel="Upload Special Power of Attorney (1 original)"
                                                files={formData.files.specialPowerOfAttorney}
                                                onFileChange={(e) => handleFileChange(e, 'specialPowerOfAttorney')}
                                                onRemoveFile={(file) => removeFile('specialPowerOfAttorney', file)}
                                             />
                                          )}
                                          {formData.isSchoolOrOrganization && (
                                             <UploadCard
                                                label="PTA Resolution"
                                                documentLabel="Upload PTA Resolution (1 original)"
                                                files={formData.files.ptaResolution}
                                                onFileChange={(e) => handleFileChange(e, 'ptaResolution')}
                                                onRemoveFile={(file) => removeFile('ptaResolution', file)}
                                             />
                                          )}
                                       </>
                                    )}
                                 </div>
                              </div>
                           </div>
                        )}

                        {currentStep === 5 && (
                           <div className="space-y-6 h-[calc(100vh-24rem)] sm:h-[500px] overflow-y-auto pr-2 sm:pr-3 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:bg-gray-200 dark:[&::-webkit-scrollbar-thumb]:bg-gray-700 [&::-webkit-scrollbar-thumb]:rounded-full">
                              <div className="space-y-4">
                                 {formData.requestType === 'Cutting' && verifiedInventoryData && (
                                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                                       <h4 className="font-semibold text-green-700 dark:text-green-400 mb-2">
                                          Verified Inventory Request
                                       </h4>
                                       <p className="text-sm text-green-600 dark:text-green-300">
                                          Application Number: {previousApplicationNumber}
                                       </p>
                                    </div>
                                 )}

                                 <div className="bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-gray-100 dark:border-gray-700">
                                    <h3 className="text-base sm:text-lg font-semibold mb-4 bg-gradient-to-r from-green-700 to-green-500 dark:from-green-500 dark:to-green-300 bg-clip-text text-transparent">
                                       Application Details
                                    </h3>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                       {Object.entries(formData)
                                          .filter(([key]) =>
                                             key !== 'status' &&
                                             key !== 'dateOfSubmission' &&
                                             key !== 'files' &&
                                             key !== 'documentRequirements'
                                          )
                                          .map(([key, value]) => (
                                             <div key={key} className="bg-background border rounded-lg p-3">
                                                <h4 className="font-semibold text-green-600 dark:text-green-500 mb-1 text-sm">
                                                   {formatLabel(key)}
                                                </h4>
                                                <p className="text-foreground text-sm break-words">
                                                   {formatReviewValue(key, value)}
                                                </p>
                                             </div>
                                          ))}
                                    </div>
                                 </div>

                                 <div className="bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-gray-100 dark:border-gray-700">
                                    <h3 className="text-base sm:text-lg font-semibold mb-4 bg-gradient-to-r from-green-700 to-green-500 dark:from-green-500 dark:to-green-300 bg-clip-text text-transparent">
                                       Document Requirements
                                    </h3>
                                    <div className="space-y-2">
                                       <div className="flex items-center">
                                          <span className="text-sm text-gray-700 dark:text-gray-300">
                                             CLOA Title: {formData.isCLOA ? 'Yes' : 'No'}
                                          </span>
                                       </div>
                                       <div className="flex items-center">
                                          <span className="text-sm text-gray-700 dark:text-gray-300">
                                             Representative: {formData.isRepresentative ? 'Yes' : 'No'}
                                          </span>
                                       </div>
                                       <div className="flex items-center">
                                          <span className="text-sm text-gray-700 dark:text-gray-300">
                                             School or Organization: {formData.isSchoolOrOrganization ? 'Yes' : 'No'}
                                          </span>
                                       </div>
                                    </div>
                                 </div>

                                 <div className="bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-gray-100 dark:border-gray-700">
                                    <h3 className="text-base sm:text-lg font-semibold mb-4 bg-gradient-to-r from-green-700 to-green-500 dark:from-green-500 dark:to-green-300 bg-clip-text text-transparent">
                                       Uploaded Files
                                    </h3>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                       {Object.entries(formData.files).map(([docType, files]) => (
                                          <div key={docType} className="border-b dark:border-gray-700 pb-2">
                                             <h5 className="font-medium text-gray-700 dark:text-gray-300 mb-1 text-sm">
                                                {formatLabel(docType)}
                                             </h5>
                                             {files.length > 0 ? (
                                                <ul className="list-disc list-inside">
                                                   {files.map((file, index) => (
                                                      <li key={index} className="text-sm text-gray-600 dark:text-gray-400 truncate">
                                                         {file.name}
                                                      </li>
                                                   ))}
                                                </ul>
                                             ) : (
                                                <p className="text-sm text-gray-500 dark:text-gray-400">No files uploaded</p>
                                             )}
                                          </div>
                                       ))}
                                    </div>
                                 </div>
                              </div>
                           </div>
                        )}
                     </form>
                  </CardContent>
                  <CardFooter className="border-t border-gray-100 dark:border-gray-700 p-4 sm:p-6">
                     <div className="flex flex-col sm:flex-row justify-between items-center gap-3 w-full">
                        <div className="w-full sm:w-auto order-2 sm:order-1">
                           {currentStep > 0 && (
                              <Button
                                 type="button"
                                 variant="outline"
                                 onClick={handlePrevStep}
                                 className="w-full sm:w-auto px-4 sm:px-6 py-2 border-2 hover:bg-gray-50 dark:hover:bg-gray-700"
                                 disabled={isSubmitting || isSavingDraft}
                              >
                                 Previous
                              </Button>
                           )}
                        </div>
                        <div className="w-full sm:w-auto flex flex-col sm:flex-row gap-3 order-1 sm:order-2">
                           {currentStep < steps.length - 1 ? (
                              <Button
                                 type="button"
                                 onClick={handleNextStep}
                                 className="w-full sm:w-auto px-4 sm:px-6 py-2 bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600 text-white shadow-md hover:shadow-lg transition-all duration-200"
                                 disabled={isSubmitting || isSavingDraft}
                              >
                                 Next
                              </Button>
                           ) : (
                              <>
                                 <Button
                                    type="button"
                                    variant="outline"
                                    onClick={handleSaveAsDraft}
                                    className="w-full sm:w-auto px-4 sm:px-6 py-2 border-2 hover:bg-gray-50 dark:hover:bg-gray-700"
                                    disabled={isSubmitting || isSavingDraft}
                                 >
                                    {isSavingDraft ? (
                                       <>
                                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                          Saving...
                                       </>
                                    ) : (
                                       'Save as Draft'
                                    )}
                                 </Button>
                                 <Button
                                    type="submit"
                                    onClick={handleSubmit}
                                    className="w-full sm:w-auto px-4 sm:px-6 py-2 bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600 text-white shadow-md hover:shadow-lg transition-all duration-200"
                                    disabled={isSubmitting || isSavingDraft}
                                 >
                                    {isSubmitting ? (
                                       <>
                                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                          Submitting...
                                       </>
                                    ) : (
                                       'Submit'
                                    )}
                                 </Button>
                              </>
                           )}
                        </div>
                     </div>
                  </CardFooter>
               </Card>
            </div>
         </div>
         <Modal
            isOpen={modalOpen}
            title={modalContent.title}
            message={modalContent.message}
            onClose={() => setModalOpen(false)}
            onHome={() => navigate('/home')}
            onApplications={() => navigate('/applicationsStatus')}
         />
         <AlertDialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
            <AlertDialogContent>
               <AlertDialogHeader>
                  <AlertDialogTitle>Submit Application</AlertDialogTitle>
                  <AlertDialogDescription>
                     Are you sure you want to submit this application? Please review all details carefully before proceeding.
                  </AlertDialogDescription>
               </AlertDialogHeader>
               <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                     onClick={handleConfirmSubmit}
                     className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white"
                  >
                     {isSubmitting ? (
                        <>
                           <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                           Submitting...
                        </>
                     ) : (
                        'Submit'
                     )}
                  </AlertDialogAction>
               </AlertDialogFooter>
            </AlertDialogContent>
         </AlertDialog>

         <AlertDialog open={showDraftDialog} onOpenChange={setShowDraftDialog}>
            <AlertDialogContent>
               <AlertDialogHeader>
                  <AlertDialogTitle>Save as Draft</AlertDialogTitle>
                  <AlertDialogDescription>
                     Do you want to save your current progress as a draft? You can continue editing it later.
                  </AlertDialogDescription>
               </AlertDialogHeader>
               <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                     onClick={handleConfirmSaveAsDraft}
                     className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white"
                  >
                     {isSavingDraft ? (
                        <>
                           <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                           Saving...
                        </>
                     ) : (
                        'Save Draft'
                     )}
                  </AlertDialogAction>
               </AlertDialogFooter>
            </AlertDialogContent>
         </AlertDialog>
      </div>
   );
};

export default PLTPForm;
